import { Card, Flex, Group, Legend, RadioButtons, SelectInput, Tag, Text, Tooltip } from "components/common"
import { useSelector } from "react-redux"
import DateTimePicker from "../dateTimePicker"
import { t } from "i18next"
import dayjs from "dayjs"
import { Form } from "antd"
import { useLazyGetCalenderEventsQuery } from "features/calendar/api"
import { memo, useEffect, useMemo } from "react"
import { useLazyGetTodosCountQuery } from "features/tasks/api"
import { IconText } from "components/custom"
import DateTime from "utils/dateTime"
import { dateTimeFunction } from "utils/date"
import styles from "./index.module.less"
import { useLazyGetClassificationQuery, useLazyGetProjectsQuery } from "features/shared/api"
import { getDeadlineRules } from "../utilis"

const NextTask = memo(
  ({ isEditable, busySlotVisible, setBusySlotVisible, rules, leadDetailInfo, lead, taskTypes, form, formRef }) => {
    const userInfo = useSelector(state => state?.app?.user.info)
    const isTSRUser = userInfo?.UserDesignation?.name === "TSR"

    const formValues = Form.useWatch([], form) || {}
    const { subTask, deadLine: deadline, dateSlot, nextTask, meetingTime, completionTime } = formValues

    const [getCalendarEvents, { data: getCalendarEventsList }] = useLazyGetCalenderEventsQuery()
    const [getTodosCount, { data: todosCount }] = useLazyGetTodosCountQuery()
    const [getProjects, { data: projects }] = useLazyGetProjectsQuery()
    const [getClassifications, { data: classifications }] = useLazyGetClassificationQuery()

    const checkEventList = selectedTime => {
      for (let i = 0; i < getCalendarEventsList?.length; i++) {
        let startTime = dayjs(getCalendarEventsList[i].startTimeStamp).unix()
        let endTime = dayjs(getCalendarEventsList[i].endTimeStamp).unix()
        if (startTime <= selectedTime[0].unix() && endTime >= selectedTime[1].unix()) {
          setBusySlotVisible(true)
          break
        }
      }
    }

    const { meetingPushedDeadline, disabledDeadline, endDate } = useMemo(
      () =>
        getDeadlineRules({
          taskTypes,
          leadDetailInfo,
          setFieldsValue: form.setFieldsValue,
          nextTask,
          subTask,
          selectedTimeSlot: meetingTime || completionTime,
          showDateSlot: true,
        }),
      [taskTypes, leadDetailInfo, nextTask, subTask, meetingTime, completionTime]
    )

    const getCalenderEventsObj = useMemo(() => {
      return {
        startTime: dayjs().startOf("day").unix(),
        endTime: dayjs().endOf("day").unix(),
        userId: userInfo?.teamType === "support" ? lead?.User?.id : userInfo?.id,
      }
    }, [userInfo?.teamType, userInfo?.id, lead?.User?.id])

    useEffect(() => {
      if ((deadline || dateSlot) && userInfo && taskTypes) {
        const params = {
          deadline: dateSlot
            ? `${dateTimeFunction({ date: dateSlot?.startOf("day"), isUnix: true })},${dateTimeFunction({
                date: DateTime.getDaysAfterDateProvided(dateSlot?.startOf("day"), 6),
                isUnix: true,
              })}`
            : `${dateTimeFunction({ date: deadline?.startOf("day"), isUnix: true })},${dateTimeFunction({
                date: DateTime.getDaysAfterDateProvided(deadline?.startOf("day"), 6),
                isUnix: true,
              })}`,
          dateWiseSegregation: true,
        }
        getTodosCount({ userId: userInfo?.id, params })
      }
    }, [deadline, dateSlot, userInfo, taskTypes])

    useEffect(() => {
      if (subTask) {
        getCalendarEvents(getCalenderEventsObj)
      }
    }, [subTask, userInfo?.id, lead?.User?.id])

    useEffect(() => {
      if (formRef) {
        getProjects()
        getClassifications()
      }
    }, [formRef])

    const renderDayWiseTodoCount = data => {
      return (
        <Flex justify="space-between" gap="6px" style={{ marginTop: 10 }}>
          {data?.dateWiseCounts?.map(todo => (
            <Tooltip
              className="flexGrow"
              title={
                <Flex gap="4px" align="normal" vertical>
                  <Flex gap="16px" justify="space-between" className="mbe-4" style={{ minWidth: 220 }}>
                    <IconText
                      icon={"IconTaskList"}
                      title={t("Todos Breakdown")}
                      iconProps={{ color: todo?.color }}
                      className="bold"
                      gap="6px"
                    />
                    <span className="semiBold" style={{ color: todo?.color }}>
                      {todo?.total}
                    </span>
                  </Flex>

                  <Flex justify="space-between">
                    <Text type="secondary">{t("Calls")}</Text>
                    {todo?.calls}
                  </Flex>
                  <Flex justify="space-between">
                    <Text type="secondary">{t("Sales")}</Text>
                    {todo?.sales}
                  </Flex>
                  <Flex justify="space-between">
                    <Text type="secondary">{t("Meetings")}</Text>
                    {todo?.meetings}
                  </Flex>
                </Flex>
              }
            >
              <Card className={styles.deadlineCards} bodyStyle={{ "--ant-padding-lg": "2px" }}>
                <Flex vertical className="w-100" gap="2px">
                  <Text type="secondary" className={styles.month}>
                    {todo?.month}
                  </Text>
                  <Text className={styles.day}>{todo?.day}</Text>
                  <Tag type="light" color={todo?.color} className={styles.tag}>
                    <IconText gap="4px" align="middle" icon={"IconTaskList"} title={todo?.total} />
                  </Tag>
                </Flex>
              </Card>
            </Tooltip>
          ))}
        </Flex>
      )
    }

    const hideNextTask = subTask === "mou"
    const hideDeadline = subTask === "meeting_pushed" || nextTask === "meeting_done"
    const toDoTask = taskTypes?.todoLogic?.[subTask]

    return (
      <>
        <Legend title={t("Next Task")} hidden={hideNextTask} />
        <Group template="repeat(2, 1fr)" gap="6px 32px">
          <Form.Item
            required
            name="nextTask"
            label={t("Next Task")}
            rules={rules.concat({
              validator: async (_, value) => {
                if (hideNextTask || value === null) return Promise.resolve()
                if (!value) return Promise.reject(t("Next task is required"))
              },
            })}
            hidden={hideNextTask}
          >
            <SelectInput
              placeholder={t("Select next task")}
              options={taskTypes?.nextStatuses}
              getOptionLabel={option => option?.statusTitleTodo}
              getOptionValue={option => option?.key}
              onChange={(value, _option) => {
                form.setFieldsValue({ nextTask: value })
              }}
              disabled={(isEditable && leadDetailInfo?.isTaskStatusSales) || toDoTask?.isTodoEnforced}
            />
          </Form.Item>

          <Form.Item name="deadlineWrapper" extra={renderDayWiseTodoCount(todosCount)} hidden={hideDeadline}>
            <DateTimePicker
              fieldNames={["deadLine", "deadLineTime"]}
              fieldLabels={[t("Deadline"), t("Deadline Time")]}
              required={true}
              rules={rules}
              grouped
              range={{ startDate: dayjs(), endDate }}
              disabled={(isEditable && leadDetailInfo?.isTaskStatusSales) || disabledDeadline}
              datePickerProps={{
                disablePastTime: true,
              }}
              timePickerProps={{
                startDate: deadline,
              }}
            />
          </Form.Item>

          <Form.Item name="project" label={t("Project")}>
            <SelectInput
              disabled={!!leadDetailInfo?.contracts.length || isEditable}
              placeholder={t("Select project")}
              options={projects}
              getOptionValue={option => option.projectId}
              onChange={(value, _option) => {
                form.setFieldsValue({ project: _option })
              }}
            />
          </Form.Item>

          <Form.Item
            name="leadsClassification"
            label={t("Leads Classification")}
            required={!isTSRUser}
            rules={rules}
            disabled={!isTSRUser}
          >
            <RadioButtons options={classifications} disabled={isTSRUser || isEditable} />
          </Form.Item>

          {isTSRUser && (
            <Form.Item name="tsrClassification" label={t("TSR Classification")} required>
              <RadioButtons options={classifications} disabled={isEditable} />
            </Form.Item>
          )}

          {hideDeadline && (
            <DateTimePicker
              fieldNames={["dateSlot", "timeSlot"]}
              fieldLabels={[t("Date Slot"), t("Time Slot")]}
              range={{ startDate: dayjs(), endDate }}
              required
              rules={rules}
              isRangePicker={true}
              disabled={disabledDeadline}
              dateExtra={renderDayWiseTodoCount(todosCount)}
              timeExtra={
                <Text type="warning" hidden={!busySlotVisible}>
                  {t("User has already scheduled a meeting during this time")}
                </Text>
              }
              timePickerProps={{
                onChange: x => {
                  setBusySlotVisible(false)
                  checkEventList(x)
                },
              }}
            />
          )}
        </Group>
      </>
    )
  }
)
export default NextTask
